import { addBreadcrumb } from '@sentry/vue';
import baseAxios, { AxiosInstance, AxiosResponse, CanceledError, InternalAxiosRequestConfig } from 'axios';
import applyCaseMiddleware from 'axios-case-converter';
import { camelCase, camelCaseTransformMerge } from 'camel-case';
import { snakeCase } from 'change-case';
import { DataFetcherFunction, DataFetcherResponse, DataFetcherSort } from '@/compiler/types';
import { useNarrative } from '@/composables/useNarrative';
import usePopupNotifications from '@/composables/usePopupNotifications';
import { getLoginUrlWithCurrentPageRedirect } from '@/helpers/getLoginUrlWithCurrentPageRedirect';
import { appReady } from '@/state/authState';
import { useSessionExpiredStore } from '@/stores/overlays/sessionExpired';

export class NetworkInterceptionError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'NetworkInterceptionError';
    }
}

const client = baseAxios.create({ withXSRFToken: true });
const clientWithoutCaseMiddleware = baseAxios.create({ withXSRFToken: true });

// A blob request's error body arrives as a Blob, so the message/errors handling below would read nothing.
const parseJsonBlobErrorBody = async (error: unknown): Promise<never> => {
    const response = baseAxios.isAxiosError(error) ? error.response : undefined;
    const body: unknown = response?.data;

    if (response && body instanceof Blob && body.type.includes('json')) {
        try {
            response.data = JSON.parse(await body.text());
        } catch (parseError) {
            if (!(parseError instanceof SyntaxError)) throw parseError;
        }
    }

    return Promise.reject(error);
};

const applyInterceptors = (client: AxiosInstance): void => {
    client.interceptors.response.use(undefined, parseJsonBlobErrorBody);

    client.interceptors.request.use((request: InternalAxiosRequestConfig) => {
        request.headers.Accept = 'application/json';

        const { currentMerchantOptional, currentServiceProviderOptional } = useNarrative();

        // Prefer an explicit per-request merchant over the ambient one: a deferred/batched
        // request captures its merchant up front so a merchant switch before it is sent can't
        // scope it to the wrong merchant. Falls back to the current merchant when not set.
        const merchantId = request.__conveyrRequestConfig?.merchantId ?? currentMerchantOptional.value?.id;
        if (merchantId != null) {
            request.headers['X-Selected-Client-Id'] = merchantId.toString();
        }

        const serviceProviderId = request.__conveyrRequestConfig?.serviceProviderId ?? currentServiceProviderOptional.value?.id;
        if (serviceProviderId != null) {
            request.headers['X-Service-Provider-Id'] = serviceProviderId.toString();
        }

        request.headers['X-App-Client'] = `conveyr-ui/${(import.meta.env.VITE_RELEASE_TAG != null && import.meta.env.VITE_RELEASE_TAG !== '') ? import.meta.env.VITE_RELEASE_TAG : 'unknown'}`;

        addBreadcrumb({
            type: 'http',
            category: 'http.request',
            message: `${(request.method ?? 'GET').toUpperCase()} ${request.url}`,
            data: {
                method: (request.method ?? 'GET').toUpperCase(),
                url: request.url,
                merchant_id: request.headers['X-Selected-Client-Id'],
            },
            level: 'info',
        });

        return request;
    });

    client.interceptors.response.use((response: AxiosResponse<any>) => { // eslint-disable-line @typescript-eslint/no-explicit-any -- required for legacy compatibility; fixing would require broader type refactoring
        // A status of 0 means the request was aborted or interrupted mid-flight (e.g. the
        // user navigated away). Firefox surfaces these as a *resolved* response with an empty
        // body rather than a rejection, leaving callers to read fields off an empty string and
        // crash. Reject as a cancellation so callers' cleanup runs and isCancel() recognises it;
        // Sentry's ignoreErrors already drops "Request aborted" so this produces no noise.
        if (response.status === 0) {
            addBreadcrumb({
                type: 'http',
                category: 'http.response',
                message: `0 (aborted) ${(response.config.method ?? 'GET').toUpperCase()} ${response.config.url}`,
                data: {
                    method: (response.config.method ?? 'GET').toUpperCase(),
                    url: response.config.url,
                    status_code: 0,
                },
                level: 'info',
            });

            return Promise.reject(new CanceledError('Request aborted'));
        }

        const looksLikeHtmlPage = typeof response.data === 'string' && /^\s*<(!doctype|html)\b/i.test(response.data);
        if (looksLikeHtmlPage && response.status !== 204) {
            addBreadcrumb({
                type: 'http',
                category: 'http.response',
                message: `${response.status} ${(response.config.method ?? 'GET').toUpperCase()} ${response.config.url} (HTML page received instead of JSON — possible captive portal or network interception)`,
                data: {
                    method: (response.config.method ?? 'GET').toUpperCase(),
                    url: response.config.url,
                    status_code: response.status,
                    content_type: response.headers['content-type'],
                    response_body: String(response.data).slice(0, 1000),
                },
                level: 'warning',
            });

            usePopupNotifications().notifyError('Your network appears to be intercepting requests. Please check your connection (e.g. captive portal or proxy) and try again.');

            throw new NetworkInterceptionError('HTML response received instead of JSON — possible captive portal or network interception');
        }

        const shouldNotify = response.config.__conveyrRequestConfig?.notify !== false;

        if (shouldNotify && typeof response.data.message === 'string' && response.data.message.length > 0) {
            usePopupNotifications().notifySuccess(response.data.message);
        }

        addBreadcrumb({
            type: 'http',
            category: 'http.response',
            message: `${response.status} ${(response.config.method ?? 'GET').toUpperCase()} ${response.config.url}`,
            data: {
                method: (response.config.method ?? 'GET').toUpperCase(),
                url: response.config.url,
                status_code: response.status,
                response_body: JSON.stringify(response.data).slice(0, 1000),
            },
            level: 'info',
        });

        const sessionExpiresAt = response.headers['x-session-expires-at'];
        if (typeof sessionExpiresAt === 'string' && sessionExpiresAt !== '') {
            useSessionExpiredStore().setSessionExpiresAt(sessionExpiresAt);
        }

        return response;
    }, (error) => {
        if (baseAxios.isAxiosError(error) && error.response) {
            addBreadcrumb({
                type: 'http',
                category: 'http.response',
                message: `${error.response.status} ${(error.config?.method ?? 'GET').toUpperCase()} ${error.config?.url}`,
                data: {
                    method: (error.config?.method ?? 'GET').toUpperCase(),
                    url: error.config?.url,
                    status_code: error.response.status,
                    response_body: JSON.stringify(error.response.data).slice(0, 1000),
                },
                level: 'error',
            });
        }

        if (error.response === undefined) {
            return Promise.reject(error);
        }

        const errorSessionExpiresAt = error.response.headers['x-session-expires-at'];
        if (typeof errorSessionExpiresAt === 'string' && errorSessionExpiresAt !== '') {
            useSessionExpiredStore().setSessionExpiresAt(errorSessionExpiresAt);
        }

        // Check if notifications should be skipped
        const shouldNotify = error.config?.__conveyrRequestConfig?.notify !== false;

        if (error.response.status === 401 || error.response.status === 419) {
            if (appReady.value) {
                useSessionExpiredStore().setSessionExpired(true);
            } else {
                window.location.href = getLoginUrlWithCurrentPageRedirect();
            }
            // Silent background callers must settle so they can stop their timers and waits.
            if (!shouldNotify) return Promise.reject(error);
            return new Promise(() => {});
        }

        if (!shouldNotify) {
            return Promise.reject(error);
        }

        switch (error.response.status) {
            case 402:
                window.location.href = '/settings#/subscription';
                break;
            case 403:
                if (typeof (error.response.data.message) !== 'undefined') {
                    handle403(error);
                }
                break;
            case 404:
                if (typeof (error.response.data.message) !== 'undefined') {
                    handle404(error);
                }
                break;
            case 405:
                handle405();
                break;
            case 408:
                handle408(error);
                break;
            case 409:
                handle409(error);
                break;
            case 413:
                handle413(error);
                break;
            case 429:
                handle429(error);
                break;
            case 400:
            case 422:
                if (typeof (error.response.data.errors) !== 'undefined') {
                    handle422(error.response.data.errors);
                } else if (error.response.data.message != null) {
                    usePopupNotifications().notifyError(error.response.data.message);
                }
                break;
            case 500:
                if (typeof (error.response.data.message) !== 'undefined') {
                    handle500(error);
                }
                break;
            case 502:
                handle502(error);
                break;
            case 503:
                handle503(error);
                break;
            case 504:
                handle504(error);
                break;
            case 524:
                handle524(error);
                break;
        }

        return Promise.reject(error);
    });
};

if (import.meta.env.DEV) {
    import('@/tests/mockRequests').then(({ applyMocks }) => applyMocks(client));
}
applyInterceptors(client);
applyInterceptors(clientWithoutCaseMiddleware);

export const axiosWithoutCaseMiddleware = clientWithoutCaseMiddleware;

applyCaseMiddleware(client, {
    caseFunctions: {
        camel: (input) => {
            return camelCase(input, { transform: camelCaseTransformMerge });
        },
        snake: (input) => {
            return snakeCase(input, {
                split: (value) => {
                    const result = value
                        .replace(/([a-z0-9])([A-Z])/g, '$1 $2') // Split: lowercase/number followed by uppercase
                        .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2') // Split: uppercase followed by uppercase+lowercase
                        .replace(/([a-z])(\d)/gi, '$1 $2'); // Split: letter followed by number

                    // Then, strip unwanted characters and split into words
                    return result.split(/[^A-Z0-9[\]]+/gi).filter(Boolean);
                },
            });
        },
    },
});

interface AxiosErrorWithResponse {
    response: {
        data: {
            message?: string | null
            errors?: unknown
        }
    }
}

function isRecord(val: unknown): val is Record<string, unknown> {
    return typeof val === 'object' && val !== null && !Array.isArray(val);
}

function handle422(errors: unknown): void {
    if (Array.isArray(errors)) {
        errors.forEach((error) => {
            handle422(error);
        });

        return;
    }

    if (typeof errors === 'string') {
        usePopupNotifications().notifyError(errors);

        return;
    }

    if (typeof errors === 'object' && errors !== null && 'message' in errors && typeof errors.message === 'string') {
        usePopupNotifications().notifyError(errors.message);

        return;
    }

    if (isRecord(errors)) {
        Object.keys(errors).forEach((key) => {
            const errorFromKey = errors[key];
            if (typeof errorFromKey === 'string') {
                usePopupNotifications().notifyError(errorFromKey);

                return;
            }

            if (Array.isArray(errorFromKey)) {
                errorFromKey.forEach((error: unknown) => {
                    if (typeof error === 'string') {
                        usePopupNotifications().notifyError(error);
                    }
                });
            }
        });
    }
}

function handle503(errors: AxiosErrorWithResponse): void {
    if (errors.response.data.message != null) {
        usePopupNotifications().notifyError(errors.response.data.message);
    } else {
        usePopupNotifications().notifyError('We are unable to connect to the third-party service at this time. Please try again later.');
    }
}

function handle502(errors: AxiosErrorWithResponse): void {
    if (errors.response.data.message != null) {
        usePopupNotifications().notifyError(errors.response.data.message);
    } else {
        usePopupNotifications().notifyError('The server is temporarily unavailable. Please try again in a moment.');
    }
}

function handle504(errors: AxiosErrorWithResponse): void {
    if (errors.response.data.message != null) {
        usePopupNotifications().notifyError(errors.response.data.message);
    } else {
        usePopupNotifications().notifyError('The request timed out. Please try again.');
    }
}

function handle524(errors: AxiosErrorWithResponse): void {
    if (errors.response.data.message != null) {
        usePopupNotifications().notifyError(errors.response.data.message);
    } else {
        usePopupNotifications().notifyError('The request timed out. Please try again.');
    }
}

function handle429(errors: AxiosErrorWithResponse): void {
    if (errors.response.data.message != null) {
        usePopupNotifications().notifyError(errors.response.data.message);
    } else {
        usePopupNotifications().notifyError('Too many attempts. Please wait a moment before trying again.');
    }
}

function handle413(errors: AxiosErrorWithResponse): void {
    if (errors.response.data.message != null) {
        usePopupNotifications().notifyError(errors.response.data.message);
    } else {
        usePopupNotifications().notifyError('The submitted data is too large. Please try reducing the amount of data or uploading fewer files at once.');
    }
}

function handle409(errors: AxiosErrorWithResponse): void {
    if (errors.response.data.message != null) {
        usePopupNotifications().notifyError(errors.response.data.message);
    } else {
        usePopupNotifications().notifyError('The request could not be completed due to a conflict with the current state of the resource. Please check the data and try again.');
    }
}

function handle500(errors: AxiosErrorWithResponse): void {
    if (errors.response.data.message != null) {
        usePopupNotifications().notifyError(errors.response.data.message);
    } else {
        usePopupNotifications().notifyError('Server error processing this request. Please report this error to our support team.');
    }

    showSentryFeedback();
}

function handle403(errors: AxiosErrorWithResponse): void {
    if (errors.response.data.message == null) {
        usePopupNotifications().notifyError('This action is unauthorized.');
    } else {
        usePopupNotifications().notifyError(errors.response.data.message);
    }
}

function handle404(errors: AxiosErrorWithResponse): void {
    if (errors.response.data.message == null) {
        usePopupNotifications().notifyError('The route could not be found, please report this error to our support team.');
    } else {
        usePopupNotifications().notifyError(`${errors.response.data.message} Please report this error to our support team.`);
    }
}

function handle405(): void {
    usePopupNotifications().notifyError('Method not allowed. Please report this error to our support team.');
}

function handle408(errors: AxiosErrorWithResponse): void {
    if (errors.response.data.message != null) {
        usePopupNotifications().notifyError(errors.response.data.message);
    } else {
        usePopupNotifications().notifyError('The request timed out. Please try again.');
    }
}

function showSentryFeedback(): void {
    import('@/composables/useSentry').then((module) => {
        module.showSentryFeedback();
    });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- required for legacy compatibility; fixing would require broader type refactoring
function normalizePaginationMeta<T>(data: any): DataFetcherResponse<T> {
    if (data.meta != null && 'current_page' in data.meta) {
        data.meta = {
            ...data.meta,
            currentPage: data.meta.current_page,
            perPage: data.meta.per_page,
            lastPage: data.meta.last_page,
        };
    } else if ('current_page' in data && !('meta' in data)) {
        data.currentPage = data.current_page;
        data.perPage = data.per_page;
        data.lastPage = data.last_page;
    }

    return data;
}

export const fetchPaginatedDataFunction = <T>(
    route: string,
    preRequest?: (uri: URL) => URL,
    axiosInstance: AxiosInstance = client,
): DataFetcherFunction<T> => {
    const dataFetcher: DataFetcherFunction<T> = async (
        page: number,
        pageSize: number,
        sorts: DataFetcherSort[],
        filters: string,
    ): Promise<DataFetcherResponse<T>> => {
        let uri = new URL(route, window.location.origin);

        uri.searchParams.append('page', page.toString());
        uri.searchParams.append('per_page', pageSize.toString());
        if (filters.length > 0) {
            uri.searchParams.append('q', filters);
        }

        sorts.forEach((sort, index) => {
            Object.entries(sort).forEach(([key, value]) => {
                uri.searchParams.append(`sorts[${index}][${key}]`, value);
            });
        });

        if(preRequest) {
            uri = preRequest(uri);
        }

        const response = await axiosInstance.get<DataFetcherResponse<T>>(uri.toString());

        return normalizePaginationMeta<T>(response.data);
    };

    return dataFetcher;
};

export const fetchPaginatedDataFunctionWithMapper = <TRaw, TMapped>(
    route: string,
    mapper: (item: TRaw) => TMapped,
    preRequest?: (uri: URL) => URL,
    axiosInstance: AxiosInstance = client,
): DataFetcherFunction<TMapped> => {
    const fetcher = fetchPaginatedDataFunction<TRaw>(route, preRequest, axiosInstance);

    return async (...args) => {
        const response = await fetcher(...args);
        return { ...response, data: response.data.map(mapper) };
    };
};

export default client;
